import React, { Suspense } from 'react';
import {
  Box,
  Grid,
  GridItem,
  VStack,
  Heading,
  Text,
  Flex,
  Divider,
  Spinner,
} from '@chakra-ui/react';

// Import game components
import GamePanel from '../game/components/GameUI/GamePanel';
import GameInfo from '../game/components/GameUI/GameInfo';
import GameControls from '../game/components/GameUI/GameControls';
import PoolInfo from '../game/components/GameUI/PoolInfo';

// Import providers
import { GameProvider } from '../game/context/GameContext';
import { AuctionProvider } from '../context/AuctionContext';
import { useBalance } from '../context/BalanceContext';
import { CleanAuctionProvider } from '../context/CleanAuctionContext';

/**
 * MainCardGame component
 * 
 * This component replicates the exact structure of the main game from the /game route
 * It includes the three-panel layout with game info, game panel, and game controls
 */
const MainCardGame = () => {
  // Use the existing BalanceContext instead of creating a new one
  const balanceContext = useBalance();
  return (
    <AuctionProvider>
      <CleanAuctionProvider>
          <GameProvider>
            <Box p={3}>
        <Grid
          templateColumns={{ base: "1fr", lg: "1.7fr 8.3fr 2fr" }}
          gap={5}
          h={{ base: "auto", lg: "calc(100vh - 350px)" }}
          minH={{ base: "600px", lg: "600px" }}
          mb={{ base: 12, lg: 24 }}
        >
          {/* Left Panel - Game Info */}
          <GridItem h={{ base: "auto", lg: "100%" }} display={{ base: "none", lg: "block" }}>
            <Box
              bg="gray.800"
              borderRadius="lg"
              p={4}
              h="100%"
              border="1px solid"
              borderColor="purple.500"
              boxShadow="0 4px 12px rgba(0, 0, 0, 0.2)"
              overflow="auto"
              width="100%"
              maxW="300px"
            >
              <VStack spacing={4} align="stretch">
                <GameInfo />
                <Divider />
                <PoolInfo />
                <Divider />
                
                {/* Last Winner Section */}
                <Box 
                  p={4} 
                  bg="whiteAlpha.100" 
                  borderRadius="md" 
                  boxShadow="sm"
                  _hover={{ bg: "whiteAlpha.200" }}
                  transition="all 0.3s"
                >
                  <Heading size="sm" color="gray.300" mb={2}>Last Winner</Heading>
                  <VStack align="stretch" spacing={2}>
                    <Text fontSize="sm" color="gray.400">Address:</Text>
                    <Text fontSize="xs" fontFamily="monospace" bg="gray.700" p={2} borderRadius="md" overflow="hidden" textOverflow="ellipsis">
                      0x7F5Dd36B85D27c3B1674e93F63F2e9C5F94E1D9F
                    </Text>
                    <Text fontSize="sm" color="gray.400" mt={2}>Prize:</Text>
                    <Text fontSize="lg" fontWeight="bold" color="green.300">
                      125.00 JINU
                    </Text>
                  </VStack>
                </Box>
                
                <Divider />
                
                {/* VRF Display */}
                <Box 
                  p={4} 
                  bg="whiteAlpha.100" 
                  borderRadius="md" 
                  boxShadow="sm"
                  _hover={{ bg: "whiteAlpha.200" }}
                  transition="all 0.3s"
                >
                  <Heading size="sm" color="gray.300" mb={2}>VRF Verification</Heading>
                  <VStack align="stretch" spacing={2}>
                    <Text fontSize="sm" color="gray.400">Game Seed:</Text>
                    <Text fontSize="xs" fontFamily="monospace" bg="gray.700" p={2} borderRadius="md">
                      {Math.random().toString(36).substring(2, 15)}
                    </Text>
                    <Text fontSize="sm" color="gray.400" mt={1}>VRF Proof:</Text>
                    <Text fontSize="xs" fontFamily="monospace" bg="gray.700" p={2} borderRadius="md">
                      {Math.random().toString(36).substring(2, 15)}
                    </Text>
                  </VStack>
                </Box>
              </VStack>
            </Box>
          </GridItem>

          {/* Center Panel - Game Area */}
          <GridItem h={{ base: "auto", lg: "100%" }}>
            <Box
              bg="gray.800"
              borderRadius="lg"
              p={{ base: 2, md: 4 }}
              h="100%"
              border="1px solid"
              borderColor="purple.500"
              boxShadow="0 4px 12px rgba(0, 0, 0, 0.2)"
              position="relative"
              overflow="hidden"
            >
              <Suspense fallback={<Flex h="500px" justify="center" align="center"><Spinner size="xl" color="gray.500" /></Flex>}>
                <GamePanel />
              </Suspense>
            </Box>
          </GridItem>

          {/* Right Panel - Game Controls */}
          <GridItem h={{ base: "auto", lg: "100%" }}>
            <Box
              bg="gray.800"
              borderRadius="lg"
              p={4}
              h="100%"
              border="1px solid"
              borderColor="purple.500"
              boxShadow="0 4px 12px rgba(0, 0, 0, 0.2)"
            >
              <GameControls />
            </Box>
          </GridItem>
        </Grid>
            </Box>
          </GameProvider>
      </CleanAuctionProvider>
    </AuctionProvider>
  );
};

export default MainCardGame;
